// covered-by: packages/schemas/src/christo/__tests__/askPunttBriefing.test.ts
/**
 * AskPuntt V2 home briefing contracts.
 *
 * The LLM writes a typed action stack; Carrie never invents cards. An empty
 * briefing (no actions, guarantee.acted === false) is a valid response when
 * the producer has not run yet.
 */
import { z } from "zod";

export const askPunttInterruptPolicySchema = z.enum([
  "silent",
  "badge",
  "card",
]);
export type AskPunttInterruptPolicy = z.infer<
  typeof askPunttInterruptPolicySchema
>;

export const askPunttActionConfidenceSchema = z.enum([
  "confident",
  "preview",
  "suggestion",
]);
export type AskPunttActionConfidence = z.infer<
  typeof askPunttActionConfidenceSchema
>;

export const askPunttActionPrimarySchema = z.enum(["approve", "preview"]);
export type AskPunttActionPrimary = z.infer<typeof askPunttActionPrimarySchema>;

export const askPunttActionSecondarySchema = z.enum([
  "digDeeper",
  "notMine",
  "notNow",
]);
export type AskPunttActionSecondary = z.infer<
  typeof askPunttActionSecondarySchema
>;

export const askPunttBriefingPrepSchema = z.object({
  assetsRead: z.number().int().nonnegative(),
  approvalFilesRead: z.number().int().nonnegative(),
});
export type AskPunttBriefingPrep = z.infer<typeof askPunttBriefingPrepSchema>;

export const askPunttBriefingFindingsSchema = z.object({
  youCanSettle: z.number().int().nonnegative(),
  forExperts: z.number().int().nonnegative(),
  /** Approximate on purpose — never render as a precise count. */
  unsure: z.number().int().nonnegative(),
});
export type AskPunttBriefingFindings = z.infer<
  typeof askPunttBriefingFindingsSchema
>;

export const askPunttBriefingGuaranteeSchema = z.object({
  /** False until the user resolves an action ("Changed nothing"). */
  acted: z.boolean(),
});
export type AskPunttBriefingGuarantee = z.infer<
  typeof askPunttBriefingGuaranteeSchema
>;

export const askPunttRememberedContextSchema = z.object({
  id: z.string(),
  text: z.string(),
  source: z.enum(["project_instruction", "session", "user_model"]),
  correctable: z.boolean(),
});
export type AskPunttRememberedContext = z.infer<
  typeof askPunttRememberedContextSchema
>;

export const askPunttActionCardSchema = z.object({
  id: z.string(),
  title: z.string(),
  rationale: z.string(),
  impact: z.string(),
  reversible: z.boolean(),
  confidence: askPunttActionConfidenceSchema,
  basedOn: z.array(z.string()),
  primary: askPunttActionPrimarySchema,
  secondary: z.array(askPunttActionSecondarySchema),
});
export type AskPunttActionCard = z.infer<typeof askPunttActionCardSchema>;

export const askPunttBriefingSchema = z.object({
  sessionId: z.string(),
  greeting: z.string(),
  prep: askPunttBriefingPrepSchema,
  findings: askPunttBriefingFindingsSchema,
  guarantee: askPunttBriefingGuaranteeSchema,
  rememberedContext: z.array(askPunttRememberedContextSchema),
  actions: z.array(askPunttActionCardSchema),
  logRef: z.string().nullable(),
  interruptPolicy: askPunttInterruptPolicySchema,
  dismissed: z.boolean().optional(),
});
export type AskPunttBriefing = z.infer<typeof askPunttBriefingSchema>;

export const askPunttBriefingLogEntrySchema = z.object({
  id: z.string(),
  at: z.string(),
  assetId: z.string().optional(),
  label: z.string(),
});
export type AskPunttBriefingLogEntry = z.infer<
  typeof askPunttBriefingLogEntrySchema
>;

export const getAskPunttBriefingRequestSchema = z.object({
  query: z.object({}).optional(),
});
export const getAskPunttBriefingResponseSchema = askPunttBriefingSchema;
export type GetAskPunttBriefingResponse = AskPunttBriefing;

export const getAskPunttBriefingLogRequestSchema = z.object({
  query: z.object({}).optional(),
});
export const getAskPunttBriefingLogResponseSchema = z.object({
  entries: z.array(askPunttBriefingLogEntrySchema),
});
export type GetAskPunttBriefingLogResponse = z.infer<
  typeof getAskPunttBriefingLogResponseSchema
>;

export const askPunttActionResolutionSchema = z.enum([
  "approved",
  "dig_deeper",
  "not_mine",
  "not_now",
]);
export type AskPunttActionResolution = z.infer<
  typeof askPunttActionResolutionSchema
>;

export const resolveAskPunttBriefingActionRequestSchema = z.object({
  params: z.object({ actionId: z.string() }),
  body: z.object({
    resolution: askPunttActionResolutionSchema,
    note: z.string().optional(),
  }),
});
export const resolveAskPunttBriefingActionResponseSchema = z.object({
  briefing: askPunttBriefingSchema,
  resolvedActionId: z.string(),
});
export type ResolveAskPunttBriefingActionResponse = z.infer<
  typeof resolveAskPunttBriefingActionResponseSchema
>;

export const patchAskPunttBriefingMemoryRequestSchema = z.object({
  params: z.object({ id: z.string() }),
  body: z.object({
    text: z.string().min(1),
  }),
});
export const patchAskPunttBriefingMemoryResponseSchema = z.object({
  briefing: askPunttBriefingSchema,
  memory: askPunttRememberedContextSchema,
  notified: z.boolean(),
});
export type PatchAskPunttBriefingMemoryResponse = z.infer<
  typeof patchAskPunttBriefingMemoryResponseSchema
>;

export const dismissAskPunttBriefingRequestSchema = z.object({
  body: z.object({}).optional(),
});
export const dismissAskPunttBriefingResponseSchema = z.object({
  briefing: askPunttBriefingSchema,
});
export type DismissAskPunttBriefingResponse = z.infer<
  typeof dismissAskPunttBriefingResponseSchema
>;
